import React, { useRef, useEffect, useState } from 'react';
import classnames from 'classnames';

import {
  Fab,
  Grid,
  IconButton,
  Toolbar,
  Typography,
  AppBar,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import AccountDropdown from '@hydra/common/components/TopNavigation/AccountDropdown';
import HelpDropdown from '@hydra/common/components/TopNavigation/HelpDropdown';
import { Icon, Translate } from '@hydra/common/components';
import MagicBar from '@hydra/common/components/MagicBar';

import { useFeatureFlags } from '@hydra/common/components/FeatureFlags/featureFlagContext';

import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    height: 60,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  toolbar: { padding: theme.spacing(0, 1) },
  navigationActionsContainer: { display: 'flex' },
  magicBarContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  userActionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  hiddenXs: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex'
    }
  },
  visibleXs: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex !important'
    }
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    left: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  hidden: { display: 'none' },
  helpDropdownButton: {
    fontWeight: theme.typography.fontWeightSemibold,
    color: theme.palette.grey[500]
  }
}));

interface TopNavigationProps {
  sideNavToggle?: Function | undefined
}

export default function TopNavigation ({ sideNavToggle }: TopNavigationProps) {
  const classes = useStyles();
  const { locale, translate } = useI18n();
  const [expandedMagicbar, setExpandedMagicbar] = useState(false);
  const [kabinetOpen, setKabinetOpen] = useState(false);

  const featureFlags = useFeatureFlags();
  const showKabinet = featureFlags && featureFlags.showKabinet;

  function onMagicbarFocus () {
    if (!expandedMagicbar) {
      handleMagicbar();
    }
  }

  function onMagicbarBlur () {
    handleMagicbar();
  }

  function handleMagicbar () {
    setExpandedMagicbar(!expandedMagicbar);
  }
  const branding = (
    <IconButton
      href={`/${locale}/overview`}
      title={`Woorank | ${translate('baseline')}`}
    >
      <Typography variant='srOnly'>
        {`Woorank | ${translate('baseline')}`}
      </Typography>
      <Icon type='logo-glyph' />
    </IconButton>
  );

  const kabinet = useRef(document.querySelector('kabinet-drawer'));
  useEffect(() => {
    if (showKabinet) {
      kabinet.current = document.querySelector('kabinet-drawer');
      kabinet.current?.addEventListener('open-kabinet', () => {
        setKabinetOpen(true);
      });
      kabinet.current?.addEventListener('close-kabinet', () => {
        setKabinetOpen(false);
      });
    }
  }, [kabinet, showKabinet]);

  function handleKabinetClick () {
    if (showKabinet) {
      if (kabinetOpen) {
        // @ts-ignore
        kabinet.current?.closeDrawer();
      } else {
        // @ts-ignore
        kabinet.current?.openDrawer();
      }
    }
  }

  return (
    <>
      <AppBar elevation={0} className={classes.root}>
        <Toolbar className={classes.toolbar}>
          <Grid
            container
            justify='space-between'
            wrap='nowrap'
          >
            <Grid
              item
              md={4}
              className={classnames(
                classes.navigationActionsContainer,
                {
                  [classes.hiddenXs]: expandedMagicbar
                }
              )}
            >
              {branding}
              {sideNavToggle && (
                <Fab
                  className={classes.fab}
                  color='secondary'
                  size='small'
                  onClick={() => { sideNavToggle(); }}
                >
                  <Icon type='menu' color='white' />
                </Fab>
              )}
            </Grid>
            <Grid
              item
              md={4}
              className={classes.magicBarContainer}
            >
              <MagicBar
                language={locale}
                focused={expandedMagicbar}
                onFocus={onMagicbarFocus}
                onBlur={onMagicbarBlur}
              />
            </Grid>
            <Grid
              item
              md={4}
              className={classes.userActionsContainer}
            >
              <div className={classes.hiddenXs}>
                <HelpDropdown
                  contentColor='gray'
                  renderTrigger={({ ...props }) => (
                    <Button
                      {...props}
                    >
                      <Typography noWrap className={classes.helpDropdownButton}>
                        <Translate content='help_dropdown_item_help' />
                      </Typography>
                    </Button>
                  )}
                />
              </div>
              <IconButton
                onClick={onMagicbarBlur}
                className={classnames(
                  classes.hidden,
                  {
                    [classes.visibleXs]: expandedMagicbar
                  }
                )}
              >
                <Icon type='cross' />
              </IconButton>
              <AccountDropdown
                renderTrigger={({ className, ...props }) => (
                  <IconButton
                    id='nav-user'
                    className={classnames(
                      className,
                      { [classes.hiddenXs]: expandedMagicbar }
                    )}
                    {...props}
                  >
                    <Icon type='user' />
                  </IconButton>
                )}
              />
              {
                showKabinet
                  ? (
                    <>
                      <IconButton
                        className={classes.hiddenXs}
                        onClick={handleKabinetClick}
                      >
                        <Icon type='boxes' />
                      </IconButton>
                      <kabinet-drawer />
                    </>
                    )
                  : null
              }
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </>
  );
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'kabinet-drawer': any;
    }
  }
}
